import React, { useEffect, useState } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from '~/common/components/ui/tooltip';
import { Button } from '~/common/components/ui/button';
import { cn } from '~/common/utils/styleUtil.ts';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ResponsiveContainer } from '~/common/components/ResponsiveContainer.tsx';

type TooltipButtonProps = {
  title?: string;
  text?: string;
  control?: React.ReactElement;
  icon: React.ReactElement;
  className?: string;
};

export function TooltipButton({ title, text, control, icon, className }: Readonly<TooltipButtonProps>) {
  return (
    <ResponsiveContainer
      mobile={<PopOverTooltipButton className={className} icon={icon} text={text} title={title} control={control} />}
      desktop={
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className={cn('mx-2 text-secondary rounded-full flex items-center justify-center', className)}
              data-testid={'tooltip-trigger'}
            >
              {icon}
            </div>
          </TooltipTrigger>
          <TooltipContent
            side={'bottom'}
            className="max-w-xs break-words whitespace-normal"
            data-testid={'tooltip-content'}
          >
            {text && <p className={'font-bold'}>{text}</p>}
            {control}
          </TooltipContent>
        </Tooltip>
      }
    />
  );
}

type PopOverTooltipButtonProps = {
  title?: string;
  text?: string;
  control?: React.ReactElement;
  icon: React.ReactElement;
  className?: string;
};

export function PopOverTooltipButton({ title, text, control, icon, className }: Readonly<PopOverTooltipButtonProps>) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsPopoverOpen(false);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [isPopoverOpen]);

  return (
    <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
      <PopoverTrigger asChild={true}>
        <Button className={cn('w-10 h-10', className)} variant={'ghost'} data-testid={'tooltip-trigger'}>
          <div className={'text-secondary rounded-full flex items-center justify-center'}>{icon}</div>
        </Button>
      </PopoverTrigger>
      <PopoverContent className={'bg-secondary text-white p-4 border-0 mx-4'}>
        {title && <p className={'font-bold'}>{title}</p>}
        {text && <p>{text}</p>}
        {control && <div>{control}</div>}
      </PopoverContent>
    </Popover>
  );
}
